import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useTheme } from '../../context/ThemeContext'
import NotificationBell from '../../components/NotificationBell'
import {
  Layers,
  MessageSquare,
  User,
  CreditCard,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import ProjectLabHome from './ProjectLabHome'
import StudentProjectLab from './StudentProjectLab'
import Messages from './Messages'
import Profile from './Profile'
import Payments from './Payments'
import Receipt from './Receipt'
import DigitalLabExperience from './DigitalLabExperience'
import DigitalLabWorkspace from './DigitalLabWorkspace'
import DigitalLabProjects from './DigitalLabProjects'
import DigitalLabResources from './DigitalLabResources'

/**
 * ProjectLabPortal
 *
 * Dedicated portal shell for Project Lab-only participants.
 * Intentional, focused navigation — NOT a filtered academic LMS.
 *
 * Identity: "SMA · Project Lab" (not "Student Portal")
 * Navigation: My Project, Digital Lab, Messages, Profile
 */
export default function ProjectLabPortal({ enrollments = [], hasProjectLabEnrollments = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { darkMode, toggleDarkMode } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Determine if payments link is relevant
  const hasInvoice = enrollments.some(
    (e) => e.invoice_status && e.invoice_status !== 'paid'
  )

  const navLinks = [
    { to: '/student/dashboard', icon: Layers, label: 'My Project' },
    { to: '/student/project-lab', icon: Layers, label: 'Project Workspace', hidden: true },
    { to: '/student/digital-lab', icon: Monitor, label: 'Digital Lab' },
    { to: '/student/messages', icon: MessageSquare, label: 'Messages' },
    ...(hasInvoice ? [{ to: '/student/payments', icon: CreditCard, label: 'Payments' }] : []),
    { to: '/student/profile', icon: User, label: 'Profile' },
  ].filter((link) => !link.hidden)

  const isActive = (to) => {
    if (to === '/student/dashboard') {
      return location.pathname === '/student/dashboard'
    }
    return location.pathname.startsWith(to)
  }

  return (
    <div className={`cinema-page min-h-screen ${darkMode ? '' : 'admin-light'}`}>
      <div className="cinema-grain" aria-hidden="true" />
      {darkMode && (
        <>
          <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-30 fixed" />
          <div className="cinema-glow-orb cinema-glow-orb-blue w-[400px] h-[400px] bottom-0 -left-20 opacity-20 fixed" />
        </>
      )}

      {/* ── Top Nav ──────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-30 cinema-glass-strong">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 lg:hidden text-cinema-muted hover:text-white transition-colors"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <img
                src={darkMode ? '/Logos/SMA LOGO  DARK MODE.png' : '/Logos/SMA LOGO  VERSION 2-01.png'}
                alt="SMA Project Lab"
                className="h-8"
              />
              <div className="hidden md:flex flex-col">
                <span className="font-display text-white font-medium text-sm leading-none">Project Lab</span>
                <span className="cinema-label mt-1 text-xs">Synergy Multimedia Academy</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/student/profile"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-soft hover:text-white transition-all"
                title="My profile"
              >
                <User size={14} className="text-cinema-gold" />
                <span className="font-sans text-xs">{user?.first_name} {user?.last_name}</span>
              </Link>
              <NotificationBell />
              <button
                onClick={toggleDarkMode}
                className="flex items-center justify-center w-9 h-9 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-muted hover:text-cinema-gold transition-all"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-red-500/40 text-cinema-muted hover:text-red-400 text-xs font-sans transition-all"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/70 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex">
        {/* ── Sidebar ──────────────────────────────────────────────── */}
        <aside className={`
          fixed top-16 left-0 bottom-0 w-56 z-40 overflow-y-auto transition-transform duration-300 ease-in-out
          cinema-glass-strong border-r border-white/5 p-4
          lg:static lg:translate-x-0 lg:flex-shrink-0 lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Project Lab identity in sidebar */}
          <div className="mb-6 pb-4 border-b border-white/8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-gold font-medium">
              Project Lab
            </span>
            {enrollments.length > 0 && enrollments[0].project_title && (
              <p className="text-xs text-cinema-soft mt-1 leading-relaxed line-clamp-2">
                {enrollments[0].project_title}
              </p>
            )}
          </div>

          <div className="space-y-1">
            {navLinks.map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-sans transition-all duration-200 ${
                  isActive(to)
                    ? 'bg-cinema-glow/10 text-cinema-glow border-l-2 border-cinema-glow'
                    : 'text-cinema-muted hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                }`}
              >
                <Icon size={16} />
                {label}
              </Link>
            ))}
          </div>
        </aside>

        {/* ── Main Content ──────────────────────────────────────────── */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 relative">
          <Routes>
            <Route
              path="dashboard"
              element={<ProjectLabHome enrollments={enrollments} />}
            />
            <Route path="project-lab" element={<StudentProjectLab />} />
            <Route path="project-lab/:enrollmentId" element={<StudentProjectLab />} />
            <Route path="digital-lab" element={<DigitalLabExperience />} />
            <Route path="digital-lab/resources" element={<DigitalLabResources />} />
            <Route path="digital-lab/resources/:id" element={<DigitalLabResources />} />
            <Route path="digital-lab/workspace" element={<DigitalLabWorkspace />} />
            <Route path="digital-lab/projects" element={<DigitalLabProjects />} />
            <Route path="digital-lab/projects/:id" element={<DigitalLabProjects />} />
            <Route path="messages" element={<Messages />} />
            <Route path="profile" element={<Profile />} />
            <Route path="payments" element={<Payments context="project-lab" />} />
            <Route path="receipts/:paymentId" element={<Receipt />} />
            {/* Default: redirect to dashboard */}
            <Route path="*" element={<Navigate to="/student/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
